/* Direct Supabase REST + apex-flow-labs backend client.
 *
 * The frontend reads catalog data straight from the apex-flow-labs Supabase
 * project via PostgREST (no /api/v1/* wrapper layer needed). Writes (Stripe
 * checkout, lead capture) hit the apex-flow-labs API endpoints.
 *
 * ENV expected:
 *   NEXT_PUBLIC_SUPABASE_URL          — e.g. https://rleowvglnvbraslessch.supabase.co
 *   NEXT_PUBLIC_SUPABASE_ANON_KEY     — read-only, safe in browser bundle
 *   NEXT_PUBLIC_BACKEND_API_BASE      — https://www.apexflowlabs.com
 *   NEXT_PUBLIC_IMAGE_PROXY_BASE      — https://www.apexflowlabs.com/api/shop/image
 *
 * RLS on the backend gates anonymous SELECTs to status='published' rows only,
 * so the anon key cannot leak draft / unpublished data even if a malicious
 * client tries.
 */

const SUPABASE_URL = (
  process.env.NEXT_PUBLIC_SUPABASE_URL ?? ""
).replace(/\/$/, "");

const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ?? "";
const SITE_URL = (process.env.NEXT_PUBLIC_SITE_URL || "https://digital.apexflowlabs.com").replace(/\/$/, "");

export const BACKEND_BASE = (
  process.env.NEXT_PUBLIC_BACKEND_API_BASE || "https://www.apexflowlabs.com"
).replace(/\/$/, "");

export const IMAGE_PROXY_BASE = (
  process.env.NEXT_PUBLIC_IMAGE_PROXY_BASE ??
  `${BACKEND_BASE}/api/shop/image`
).replace(/\/$/, "");

/* Optional direct-CDN base for bare image keys stored in the DB.
 * If set, paths that aren't already absolute URLs OR proxy paths
 * (i.e. bare keys like "wedding/template-01.jpg") are served from
 * here instead of the apex-flow-labs image proxy.
 *
 * Examples:
 *   NEXT_PUBLIC_CDN_BASE=https://pub-abc123.r2.dev
 *   NEXT_PUBLIC_CDN_BASE=https://your-bucket.s3.amazonaws.com
 *   NEXT_PUBLIC_CDN_BASE=https://cdn.apexflowlabs.com
 *
 * Brian QA: covers on /wedding and /real-estate were broken because
 * the DB has bare R2/S3 keys but the helper was prefixing every bare
 * key with the apex-flow-labs image proxy URL — which doesn't host
 * those files. Setting CDN_BASE lets Brian point bare keys directly
 * at the storage host without changing every row. */
export const CDN_BASE = (
  process.env.NEXT_PUBLIC_CDN_BASE ?? ""
).replace(/\/$/, "");

/* MOCK switch — when true, lib/api.ts returns fixtures and these functions
 * are unused. When false, every catalog read hits Supabase directly. */
export const BACKEND_LIVE =
  process.env.NEXT_PUBLIC_MOCK_API !== "true" &&
  Boolean(SUPABASE_URL) &&
  !SUPABASE_URL.includes("placeholder") &&
  Boolean(SUPABASE_ANON_KEY) &&
  !SUPABASE_ANON_KEY.includes("placeholder");

/* ── Image URL helper ──────────────────────────────────────────────────────
 *
 * Backend cells store either:
 *   1. Absolute URL  (https://...)               → pass through
 *   2. Image proxy path (/api/shop/image/...)    → prefix with backend base
 *   3. Backend-absolute path (/foo/bar.jpg)      → prefix with backend base
 *   4. Bare R2/S3 key (wedding/foo.jpg)          → prefix with CDN_BASE if set,
 *                                                  otherwise the image proxy
 *
 * Always returns a usable absolute URL the browser can fetch. The fourth
 * branch is the new one (Brian QA — wedding + real-estate covers broke
 * because rows store bare R2/S3 keys, not proxy paths, and the helper
 * was sending them to the apex-flow-labs image proxy which doesn't host
 * them). Setting NEXT_PUBLIC_CDN_BASE on the digital-art Vercel project
 * routes bare keys to the actual storage host.
 */
/* Display URL for a catalog image. For the backend proxy
 * (/api/shop/image/…) a width is ALWAYS attached: without one the proxy
 * redirects to the signed 20–27 MB original in R2, and that is exactly what
 * every wall-art PDP, every "frequently bought together" card on Notion and
 * Canva pages, and the room preview were downloading (Lighthouse 2026-09-06:
 * wall-art mobile LCP 151 s, Notion 29 s). 1000 px WebP is plenty for any
 * on-page use; thumb() in lib/img.ts overrides it for grids. The print file
 * for Printful and the buyer's hi-res download use absolutize()/the
 * backend, never this helper. */
export function imageUrl(path: string | null | undefined, width = 1000, quality = 72): string {
  if (!path) return "";
  const p = path.trim();
  if (!p) return "";
  /* Private-bucket keys (Notion covers, business-kit covers) — the proxy
   * streams these at full size whatever width is asked, so they go to our
   * own resizer, which reads the bucket directly. Absolute, so JSON-LD and
   * Open Graph consumers can use it too. */
  const apexKey = /^apex\/[^?]+\.(png|jpe?g|webp)$/i.test(p)
    ? p
    : (p.match(/\/api\/shop\/image\/(apex(?:%2F|\/)[^?]+\.(?:png|jpe?g|webp))/i)?.[1] ?? null);
  if (apexKey) {
    const w = width <= 320 ? 320 : width <= 640 ? 640 : 1024;
    return `${SITE_URL}/api/img?k=${encodeURIComponent(decodeURIComponent(apexKey))}&w=${w}`;
  }
  const abs = /^https?:\/\//i.test(p)
    ? p
    : p.startsWith("/api/shop/image/") || p.startsWith("/")
      ? `${BACKEND_BASE}${p}`
      : CDN_BASE
        ? `${CDN_BASE}/${p}`
        : `${IMAGE_PROXY_BASE}/${p}`;
  if (abs.includes("/api/shop/image/") && !/[?&]width=/.test(abs)) {
    const sep = abs.includes("?") ? "&" : "?";
    return `${abs}${sep}width=${width}&quality=${quality}`;
  }
  return abs;
}

/* ── Supabase REST fetch wrapper ───────────────────────────────────────────
 *
 * Thin PostgREST client. Always sends apikey + Authorization headers.
 * Supports `?select=`, `?<column>=eq.value`, `?limit=`, `?order=`.
 * Returns parsed JSON array; throws on non-2xx with the PostgREST error body.
 */
type RestQuery = {
  select?: string;
  /* Eq filters, e.g. { status: 'eq.published', breed: 'eq.golden-retriever' } */
  filters?: Record<string, string>;
  /* Filters needing the SAME key more than once, e.g. a range:
   *   id=gte.<lo> & id=lt.<hi>
   * `filters` is a Record so it cannot express that. Appended verbatim. */
  filterPairs?: Array<[string, string]>;
  limit?: number;
  offset?: number;
  order?: string;
  cursor?: string;
};

export async function supabaseRest<T = unknown>(
  table: string,
  q: RestQuery = {},
  init?: RequestInit & { revalidate?: number },
): Promise<T[]> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error(
      "supabaseRest: NEXT_PUBLIC_SUPABASE_URL or NEXT_PUBLIC_SUPABASE_ANON_KEY is not set",
    );
  }

  const params = new URLSearchParams();
  params.set("select", q.select ?? "*");
  if (q.filters) {
    for (const [k, v] of Object.entries(q.filters)) params.append(k, v);
  }
  if (q.filterPairs) {
    for (const [k, v] of q.filterPairs) params.append(k, v);
  }
  if (q.limit !== undefined) params.set("limit", String(q.limit));
  if (q.offset !== undefined) params.set("offset", String(q.offset));
  if (q.order) params.set("order", q.order);

  const url = `${SUPABASE_URL}/rest/v1/${table}?${params.toString()}`;

  const { revalidate, headers, ...rest } = init ?? {};
  /* revalidate: 0 means "never cache" — used by /dev/smoke so it always
   * reflects the live state of Supabase + RLS. Otherwise cache for the
   * caller-supplied window or 300s default (matches the apex-flow-labs
   * publish cron cadence). */
  const baseHeaders = {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    Accept: "application/json",
    ...headers,
  };
  const fetchPromise =
    revalidate === 0
      ? fetch(url, {
          cache: "no-store",
          ...rest,
          headers: baseHeaders,
        })
      : fetch(url, {
          next: { revalidate: revalidate ?? 300 },
          ...rest,
          headers: baseHeaders,
        });

  /* Build-time safety: Supabase REST hits the same pool as cluster-bomb.
   * Without a Promise.race timeout, a hung connection lets the build
   * worker run out its 60s ceiling. Same fix pattern as fetchAPI. */
  const TIMEOUT_MS = 8000;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<Response>((_, reject) => {
    timer = setTimeout(() => {
      console.error(`supabaseRest timeout: ${url} after ${TIMEOUT_MS}ms`);
      reject(new Error(`supabaseRest timeout: ${url}`));
    }, TIMEOUT_MS);
  });

  let res: Response;
  try {
    res = await Promise.race([fetchPromise, timeoutPromise]);
  } finally {
    if (timer) clearTimeout(timer);
  }

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(
      `supabaseRest failed: ${res.status} ${res.statusText}. ${JSON.stringify(errorBody)}`,
    );
  }
  return res.json() as Promise<T[]>;
}

/**
 * Returns both the data and the total count (via Prefer: count=exact).
 */
export async function supabaseRestWithCount<T = unknown>(
  table: string,
  q: RestQuery = {},
  init?: RequestInit & { revalidate?: number },
): Promise<{ data: T[]; count: number }> {
  const params = new URLSearchParams();
  params.set("select", q.select ?? "*");
  if (q.filters) {
    for (const [k, v] of Object.entries(q.filters)) params.append(k, v);
  }
  if (q.filterPairs) {
    for (const [k, v] of q.filterPairs) params.append(k, v);
  }
  if (q.limit !== undefined) params.set("limit", String(q.limit));
  if (q.offset !== undefined) params.set("offset", String(q.offset));
  if (q.order) params.set("order", q.order);

  const url = `${SUPABASE_URL}/rest/v1/${table}?${params.toString()}`;
  const { revalidate, headers, ...rest } = init ?? {};
  
  const baseHeaders = {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    Accept: "application/json",
    Prefer: "count=exact",
    ...headers,
  };

  const res = await fetch(url, {
    next: { revalidate: revalidate ?? 300 },
    ...rest,
    headers: baseHeaders,
  });

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(`supabaseRestWithCount failed: ${res.status}. ${JSON.stringify(errorBody)}`);
  }

  const data = await res.json();
  const rangeHeader = res.headers.get("content-range"); // e.g. "0-9/123"
  const total = rangeHeader ? parseInt(rangeHeader.split("/")[1] || "0", 10) : data.length;

  return { data, count: total };
}

/* ── Typed row shapes (subset of columns the frontend actually renders) ─── */

export type ShopProductRow = {
  id?: string;
  slug: string;
  sku?: string | null;
  title: string;
  /* Short description — Mad Libs template from the generator
   * pipeline ("{Style} portrait of a {breed}…"). Always populated. */
  description?: string | null;
  /* Long description — 150-260 words of genuinely distinct prose
   * per design, written by Qwen 2.5 via P2.10. Populated rolling as
   * backend's wall-art-descriptions.ts run completes (ETA 36-50hr).
   * Frontend prefers this when present; falls back to `description`
   * for unpopulated rows so partial runs ship safely. */
  description_long?: string | null;
  description_long_model?: string | null;
  description_long_generated_at?: string | null;
  breed?: string | null;
  style?: string | null;
  format?: string | null;
  /* Backend-composed image alt (stored generated column, title+breed/style,
   * never null). Frontend prefers it for the image sitemap + PDP alt; falls
   * back to composing from title/description for rows that predate it. */
  cover_image_alt?: string | null;
  hero_image_url?: string | null;
  watermarked_preview_url?: string | null;
  price_cents: number;
  family_id?: string | null;
  keywords?: string[] | null;
  metadata?: Record<string, unknown> | null;
  status?: string;
  created_at?: string;
};

export type NotionTemplateRow = {
  id?: string;
  slug: string;
  title: string;
  blueprint_slug?: string | null;
  niche_slug?: string | null;
  description?: string | null;
  cover_image_url?: string | null;
  notion_share_url?: string | null;
  price_cents: number;
  sales_count?: number | null;
  metadata?: Record<string, unknown> | null;
  status?: string;
};

export type TemplatePackRow = {
  id?: string;
  slug: string;
  title: string;
  description?: string | null;
  category_slug?: string | null;
  canva_template_url?: string | null;
  canva_design_id?: string | null;
  cover_image_url?: string | null;
  designs?: unknown;
  price_cents: number;
  original_price_cents?: number | null;
  status?: string;
};

/* ── High-level reads ──────────────────────────────────────────────────────
 *
 * One function per table + filter combo we need. Keeps the route components
 * dumb: they ask "give me X" and get back a typed array.
 */

export async function listShopProducts(opts?: {
  breed?: string;
  /* Multi-breed filter for animal-category browsing. Pass an array of
   * breed slugs — produces a PostgREST `breed=in.(a,b,c)` filter. Use
   * lib/animal-categories breedsForAnimal() to build the list. */
  breedsIn?: string[];
  /* Multi-style filter — backend's tier-3 theme categories
   * (humor, luxury, nursery, minimalist, abstract) map cleanly to
   * style values, so the category grid uses this. */
  stylesIn?: string[];
  /* Multi-format filter. Used by the profit-first sitemap tier, which needs
   * the highest-value formats without sorting the whole catalog by price --
   * an unindexed price sort over 90k rows took ~5s and timed the route out.
   * An `in.(...)` equality filter returns in ~0.2s. */
  formatsIn?: string[];
  style?: string;
  format?: string;
  productType?: string;
  /* Free-text search across title / description / breed / style. Brian
   * QA: "there should be search". Maps to PostgREST `or=(...)` with
   * ilike fuzzies. Whitespace + special chars stripped — only [a-z0-9
   * space] survive, then the term is wrapped in `*` for a fuzzy
   * contains match. Empty/short queries are ignored. */
  q?: string;
  limit?: number;
  /* PostgREST offset — used by the paginated wall-art sitemap shards
   * (sitemap-wall-art-N.xml) so we can emit every published row
   * instead of capping at limit. */
  offset?: number;
  order?: string;
  /* Pass 0 to bypass the Next.js data cache entirely — used by /dev/smoke. */
  revalidate?: number;
}): Promise<ShopProductRow[]> {
  const filters: Record<string, string> = { status: "eq.published" };
  if (opts?.breed) filters.breed = `eq.${opts.breed}`;
  if (opts?.breedsIn && opts.breedsIn.length > 0) {
    /* PostgREST `in` operator: in.(a,b,c). Slugs already URL-safe. */
    filters.breed = `in.(${opts.breedsIn.join(",")})`;
  }
  if (opts?.style) filters.style = `eq.${opts.style}`;
  if (opts?.formatsIn && opts.formatsIn.length > 0) {
    filters.format = `in.(${opts.formatsIn.join(",")})`;
  }
  if (opts?.stylesIn && opts.stylesIn.length > 0) {
    filters.style = `in.(${opts.stylesIn.join(",")})`;
  }
  if (opts?.format) filters.format = `eq.${opts.format}`;
  if (opts?.productType) {
    /* metadata->>product_type=eq.canva-template */
    filters["metadata->>product_type"] = `eq.${opts.productType}`;
  }
  /* Free-text search — sanitize and apply as a PostgREST `or` filter
   * across the searchable text columns. ilike is case-insensitive.
   *
   * IMPORTANT: only match the human-readable columns (title +
   * description). Searching the slug columns directly causes false
   * positives from substring overlap — Brian QA: "search cat returns
   * dogs" was 'cat'.includes substring of 'australian-cattle-dog'.
   * Titles already contain the breed name in spelled-out form, so the
   * buyer's intent ("golden retriever") still resolves there. */
  const rawQ = (opts?.q ?? "").trim().toLowerCase();
  const safeQ = rawQ.replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
  if (safeQ.length >= 2) {
    const orParts = [
      `title.ilike.*${safeQ}*`,
      `description.ilike.*${safeQ}*`,
    ];
    filters.or = `(${orParts.join(",")})`;
  }
  /* The cluster occasionally ships rows in shop_products that are really
   * Notion templates (metadata.notion_template = true, e.g. "Business OS
   * for Skincare Brands"). They get format=wall_art so they leak into the
   * /digital-art catalog. Filter them out here so every wall-art read
   * is automatically clean. */
  filters["metadata->>notion_template"] = "is.null";
  /* Newest first by default — Brian QA wants new images at the top.
   * Apex shop_products has 5 SKU rows per family (wall_art, sticker_sheet,
   * coloring_page, collection_grid, poster_set) — page-side dedupe by
   * family_id is what stops adjacent dupes, not sort order. */
  const order = opts?.order ?? "created_at.desc.nullslast";
  return supabaseRest<ShopProductRow>(
    "shop_products",
    {
      filters,
      limit: opts?.limit ?? 60,
      offset: opts?.offset,
      order,
    },
    opts?.revalidate !== undefined ? { revalidate: opts.revalidate } : undefined,
  );
}

/* PostgREST embedded select for shop_families relation. Pulls the
 * description_long field (written by backend's cluster-bomb run) via
 * the foreign key from shop_products.family_id → shop_families.id.
 *
 * The 2026-05-31 5k-checkpoint audit measured 0.873 similarity on a
 * 40-PDP sample from shard 3 (offset 2000-2999, well within the
 * ~5,635 populated row range). That confirmed shop_products doesn't
 * surface description_long natively — it lives only on shop_families.
 *
 * This embed surfaces it. Once backend confirms whether they want
 * Option A (server-side view), B (this embed), or C (denormalized
 * copy), we keep whichever works. The embed is the lowest-coordination
 * path — frontend ships it without waiting for backend to expand the
 * shop_products schema.
 *
 * If the embed RLS policy doesn't allow the relation to anon-key
 * reads, the response just returns null for shop_families and we
 * fall back to short description per the existing PDP fallback chain.
 * Zero breakage either way. */
const SHOP_PRODUCT_WITH_FAMILY_SELECT =
  "*,shop_families(description_long,description_long_model,description_long_generated_at)";

type ShopProductWithEmbeddedFamily = ShopProductRow & {
  shop_families?: {
    description_long?: string | null;
    description_long_model?: string | null;
    description_long_generated_at?: string | null;
  } | null;
};

/* Lift any embedded shop_families.description_long up to the row's
 * top-level description_long field so callers (PDP template, JSON-LD,
 * meta description) read it from a single consistent path regardless
 * of whether the data came from the embed or a future server-side
 * view. */
function flattenFamilyDescription(
  row: ShopProductWithEmbeddedFamily,
): ShopProductRow {
  const family = row.shop_families;
  if (!family) return row;
  return {
    ...row,
    description_long: row.description_long ?? family.description_long ?? null,
    description_long_model:
      row.description_long_model ?? family.description_long_model ?? null,
    description_long_generated_at:
      row.description_long_generated_at ??
      family.description_long_generated_at ??
      null,
  };
}

export async function getShopProductBySlug(
  slug: string,
): Promise<ShopProductRow | null> {
  const rows = await supabaseRest<ShopProductWithEmbeddedFamily>("shop_products", {
    select: SHOP_PRODUCT_WITH_FAMILY_SELECT,
    filters: { status: "eq.published", slug: `eq.${slug}` },
    limit: 1,
  });
  const row = rows[0];
  if (!row) return null;
  return flattenFamilyDescription(row);
}

/* Prev / next neighbours within the same breed (or, lacking that, the
 * full wall_art catalog) ordered by created_at — for the prev/next
 * arrows on /digital-art/[slug]. Each returned row is the FIRST sibling
 * in its family (different family_id from the current product) so the
 * buyer doesn't see the same render with a different aspect-ratio when
 * they tap "next". */
export async function getAdjacentWallArt(
  current: ShopProductRow,
): Promise<{ prev: ShopProductRow | null; next: ShopProductRow | null }> {
  const baseFilters: Record<string, string> = {
    status: "eq.published",
    format: "eq.wall_art",
  };
  if (current.breed) baseFilters.breed = `eq.${current.breed}`;
  const fetchSide = async (cmp: "lt" | "gt"): Promise<ShopProductRow | null> => {
    if (!current.created_at) return null;
    const filters = {
      ...baseFilters,
      created_at: `${cmp}.${current.created_at}`,
    };
    const order = cmp === "lt" ? "created_at.desc" : "created_at.asc";
    const rows = await supabaseRest<ShopProductRow>(
      "shop_products",
      { filters, limit: 8, order },
      { revalidate: 0 },
    ).catch(() => []);
    /* Skip rows that share the current family_id — same render. */
    return (
      rows.find(
        (r) => !r.family_id || r.family_id !== current.family_id,
      ) ?? null
    );
  };
  const [prev, next] = await Promise.all([fetchSide("lt"), fetchSide("gt")]);
  return { prev, next };
}

/* Fetch every published WALL-ART-format row sharing one family_id. Used
 * on /digital-art/[slug] to populate the size selector. The cluster
 * ships 5 SKU rows per family (wall_art, sticker_sheet, poster_set,
 * coloring_page, collection_grid), all sharing one hero image — only
 * wall_art is sold as a print on this site, so the others are filtered
 * out before they pollute the size pills. */
export async function listFamilyProducts(
  familyId: string,
): Promise<ShopProductRow[]> {
  if (!familyId) return [];
  return supabaseRest<ShopProductRow>(
    "shop_products",
    {
      filters: {
        status: "eq.published",
        family_id: `eq.${familyId}`,
        format: "eq.wall_art",
      },
      limit: 12,
    },
    { revalidate: 0 },
  );
}

export async function listNotionTemplates(opts?: {
  blueprint?: string;
  niche?: string;
  /* Free-text search against title (PostgREST `ilike`, anchored as *q*). */
  q?: string;
  /* "best-selling" | "newest" | "az" — translated to PostgREST order. */
  sort?: "best-selling" | "newest" | "az";
  limit?: number;
  order?: string;
  revalidate?: number;
}): Promise<NotionTemplateRow[]> {
  const filters: Record<string, string> = { status: "eq.published" };
  if (opts?.blueprint) filters.blueprint_slug = `eq.${opts.blueprint}`;
  if (opts?.niche) filters.niche_slug = `eq.${opts.niche}`;
  if (opts?.q) {
    /* PostgREST: title=ilike.*<q>* — wildcards encoded via %2A is fine but
     * supabase-js / our wrapper does no escaping, so users typing % chars
     * could mess up the pattern. We strip them defensively. */
    const q = opts.q.replace(/[*%]/g, "").trim();
    if (q) filters.title = `ilike.*${q}*`;
  }
  const order =
    opts?.order ??
    (opts?.sort === "newest"
      ? "created_at.desc.nullslast"
      : opts?.sort === "az"
        ? "title.asc"
        : "sales_count.desc.nullslast");
  return supabaseRest<NotionTemplateRow>(
    "apex_notion_templates",
    {
      filters,
      limit: opts?.limit ?? 60,
      order,
    },
    opts?.revalidate !== undefined
      ? { revalidate: opts.revalidate }
      : undefined,
  );
}

/* Blueprint counts for the filter chips on /notion. Pulls all published
 * rows' blueprint_slug column and tallies in JS. PostgREST caps each
 * request at max-rows (usually 1000 on hosted Supabase), so we
 * paginate via offset until we get a short page. The blueprint_slug
 * column is small (a single text field), so paginating up to a few
 * thousand rows is still cheap. */
const NOTION_PAGE_SIZE = 1000;
const NOTION_MAX_PAGES = 10; /* hard ceiling: 10k templates is plenty */

export async function getNotionBlueprintCounts(): Promise<
  Record<string, number>
> {
  const counts: Record<string, number> = {};
  for (let page = 0; page < NOTION_MAX_PAGES; page++) {
    const rows = await supabaseRest<{ blueprint_slug: string | null }>(
      "apex_notion_templates",
      {
        select: "blueprint_slug",
        filters: { status: "eq.published" },
        limit: NOTION_PAGE_SIZE,
        offset: page * NOTION_PAGE_SIZE,
      },
    );
    for (const r of rows) {
      if (r.blueprint_slug) {
        counts[r.blueprint_slug] = (counts[r.blueprint_slug] ?? 0) + 1;
      }
    }
    if (rows.length < NOTION_PAGE_SIZE) break;
  }
  return counts;
}

/* Total count of published Notion templates. Uses PostgREST's
 * Prefer: count=exact header to read the row count from
 * Content-Range without actually fetching the rows — single
 * cheap HEAD-shaped request, accurate even past the page cap. */
export async function getNotionTotalCount(): Promise<number> {
  const { count } = await supabaseRestWithCount<{ id?: string }>(
    "apex_notion_templates",
    {
      select: "id",
      filters: { status: "eq.published" },
      limit: 1,
    },
  );
  return count;
}

export async function getNotionTemplateBySlug(
  slug: string,
): Promise<NotionTemplateRow | null> {
  const rows = await supabaseRest<NotionTemplateRow>(
    "apex_notion_templates",
    {
      filters: { status: "eq.published", slug: `eq.${slug}` },
      limit: 1,
    },
  );
  return rows[0] ?? null;
}

export async function listTemplatePacks(opts?: {
  category?: string;
  limit?: number;
  offset?: number;
  order?: string;
  includeCount?: boolean;
  /* Customer-facing reads should only return templates with a live
   * link pool — otherwise buyers land on products that can't fulfill.
   * Defaults to true (customer-safe). Pass false from internal/admin/
   * smoke surfaces that need to see everything. Brian QA: "hide
   * products that don't have link pool, when I add links they show". */
  includeInactivePool?: boolean;
}): Promise<TemplatePackRow[] | { data: TemplatePackRow[]; count: number }> {
  const filters: Record<string, string> = { status: "eq.published" };
  if (opts?.category) filters.category_slug = `eq.${opts.category}`;
  if (opts?.includeInactivePool !== true) {
    filters.link_pool_active = "eq.true";
  }

  if (opts?.includeCount) {
    return supabaseRestWithCount<TemplatePackRow>("template_packs", {
      filters,
      limit: opts?.limit ?? 60,
      offset: opts?.offset,
      order: opts?.order ?? "created_at.desc",
    });
  }

  return supabaseRest<TemplatePackRow>("template_packs", {
    filters,
    limit: opts?.limit ?? 60,
    offset: opts?.offset,
    order: opts?.order ?? "created_at.desc",
  });
}

export async function getTemplatePackBySlug(
  slug: string,
  opts?: { includeInactivePool?: boolean },
): Promise<TemplatePackRow | null> {
  const filters: Record<string, string> = {
    status: "eq.published",
    slug: `eq.${slug}`,
  };
  if (opts?.includeInactivePool !== true) {
    filters.link_pool_active = "eq.true";
  }
  const rows = await supabaseRest<TemplatePackRow>("template_packs", {
    filters,
    limit: 1,
  });
  return rows[0] ?? null;
}

/* ── Distinct values for filter rails ──────────────────────────────────────
 *
 * PostgREST doesn't return DISTINCT directly via REST — but we can fake it
 * cheaply by listing a large limit and reducing client-side. Cached at edge
 * for an hour so the cost is negligible.
 */

export async function listProductFacets(): Promise<{
  breeds: string[];
  styles: string[];
  formats: string[];
}> {
  /* PostgREST has a server-side max-rows cap (Supabase defaults to 1000).
   * `?limit=8000` is silently truncated, so a single fetch only saw the
   * first ~1K rows alphabetically — which clustered onto 4 unique breeds
   * because each breed has dozens of variants ordered together.
   *
   * Workaround: page through the table in 1K-row windows in parallel,
   * dedupe in JS. The catalog is ~16K rows so 20 pages is the safe
   * ceiling; we stop a page early when an empty batch comes back. */
  const PAGE = 1000;
  const MAX_PAGES = 20;
  const baseFilters = { status: "eq.published" };

  const fetchPage = (offset: number) =>
    supabaseRest<Pick<ShopProductRow, "breed" | "style" | "format">>(
      "shop_products",
      {
        select: "breed,style,format",
        filters: baseFilters,
        order: "breed.asc.nullslast",
        limit: PAGE,
        offset,
      },
    ).catch(() => [] as Array<Pick<ShopProductRow, "breed" | "style" | "format">>);

  const pages = await Promise.all(
    Array.from({ length: MAX_PAGES }, (_, i) => fetchPage(i * PAGE)),
  );

  const breeds = new Set<string>();
  const styles = new Set<string>();
  const formats = new Set<string>();
  for (const page of pages) {
    for (const r of page) {
      if (r.breed) breeds.add(r.breed);
      if (r.style) styles.add(r.style);
      if (r.format) formats.add(r.format);
    }
  }
  return {
    breeds: [...breeds].sort(),
    styles: [...styles].sort(),
    formats: [...formats].sort(),
  };
}

/* Per-breed product count — used by /n to populate niche card counters
 * when the apex-flow-labs niches API reports zeros (it's not keeping its
 * `asset_count` rollup in sync with shop_products).
 *
 * Reuses the same paged pattern as listProductFacets so we sweep around
 * the PostgREST max-rows cap. Returns a slug→count map; only published
 * wall_art rows count toward "prints" since that's what /digital-art
 * actually sells. */
export async function listBreedCounts(): Promise<Record<string, number>> {
  const PAGE = 1000;
  const MAX_PAGES = 20;
  const fetchPage = (offset: number) =>
    supabaseRest<Pick<ShopProductRow, "breed">>("shop_products", {
      select: "breed",
      filters: { status: "eq.published", format: "eq.wall_art" },
      order: "breed.asc.nullslast",
      limit: PAGE,
      offset,
    }).catch(() => [] as Array<Pick<ShopProductRow, "breed">>);

  const pages = await Promise.all(
    Array.from({ length: MAX_PAGES }, (_, i) => fetchPage(i * PAGE)),
  );
  const counts: Record<string, number> = {};
  for (const page of pages) {
    for (const r of page) {
      if (!r.breed) continue;
      counts[r.breed] = (counts[r.breed] ?? 0) + 1;
    }
  }
  return counts;
}

/* ── Checkout (writes — POST to apex-flow-labs API) ────────────────────────
 *
 * Single endpoint. The backend dispatches by `kind`:
 *   POST /api/shop/checkout
 *   Body: {
 *     items: [{ kind: 'product' | 'bundle', id: string, quantity?: number }],
 *     successPath?: string,
 *     cancelPath?:  string,
 *   }
 *   Returns: { url: 'https://checkout.stripe.com/...' }
 *
 * Cap is 5 items per call. `id` is the row PK (NOT the slug). Pages that
 * already have the row from Supabase pass `row.id` through.
 */

/* Backend's /api/shop/checkout dispatches by `kind`. The schema currently
 * accepts product | bundle | notion-template. Extending again? Add the new
 * kind here AND coordinate with apex-flow-labs so the API doesn't 4xx. */
export type CheckoutItemKind = "product" | "bundle" | "notion-template";

export type CheckoutItem = {
  kind: CheckoutItemKind;
  id: string;
  quantity?: number;
};

export async function startCheckoutCall(input: {
  items: CheckoutItem[];
  successPath?: string;
  cancelPath?: string;
}): Promise<{ url: string }> {
  if (input.items.length === 0) {
    throw new Error("startCheckoutCall: items[] is empty");
  }
  if (input.items.length > 5) {
    throw new Error("startCheckoutCall: max 5 items per checkout");
  }
  /* Hit our same-origin Next route (src/app/api/shop/checkout/route.ts).
   * That handler forwards to apex-flow-labs server-side, dodging the
   * cross-origin CORS preflight that fails for digital.apexflowlabs.com →
   * www.apexflowlabs.com. */
  const res = await fetch(`/api/shop/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(
      `checkout failed ${res.status}: ${body.slice(0, 240) || "no body"}`,
    );
  }
  return res.json() as Promise<{ url: string }>;
}

/* Convenience wrappers — match the call sites, hide the items[] shape. */
export function startSingleCheckout(
  productId: string,
  opts?: { successPath?: string; cancelPath?: string },
) {
  return startCheckoutCall({
    items: [{ kind: "product", id: productId, quantity: 1 }],
    successPath: opts?.successPath,
    cancelPath: opts?.cancelPath,
  });
}

export function startBundleCheckout(
  bundleId: string,
  opts?: { successPath?: string; cancelPath?: string },
) {
  return startCheckoutCall({
    items: [{ kind: "bundle", id: bundleId, quantity: 1 }],
    successPath: opts?.successPath,
    cancelPath: opts?.cancelPath,
  });
}

/* ── Wall-art sitemap shard reader ────────────────────────────────────
 *
 * Enumerates every published wall-art row whose id falls in one UUID
 * shard range. Used ONLY by the wall-art sitemap routes.
 *
 * WHY THIS DOES NOT CALL listShopProducts. That helper always applies
 *
 *     metadata->>notion_template=is.null
 *
 * to strip Notion rows that leak into wall_art. It is an unindexed
 * JSONB expression and it is the measured cause of the sitemap 500s.
 * Isolated 3/3 on 2026-08-18 with a control — the identical query with
 * that one predicate removed:
 *
 *   status+format+order=id.asc, select=*        200 / 0.84s 0.83s 0.67s
 *   ...the same PLUS the notion predicate       500 / 8.2s  8.2s  8.1s
 *
 * So the predicate is dropped from SQL and applied in JS below. The
 * result is identical — the filter is exact either way — and it costs
 * nothing on a page of at most a few thousand rows.
 *
 * PostgREST is configured db-max-rows=1000: a request for 10,000 rows
 * returns exactly 1000 with no error and no signal. Anything wanting
 * more than 1000 MUST keyset-loop or it truncates silently, which is
 * the exact failure /api/sitemap-stats already warns about. Hence the
 * loop, and hence the hard throw rather than a short read. */
const POSTGREST_MAX_ROWS = 1000;

/* At ~78k rows over 256 shards a shard holds ~305 rows, so one query.
 * The cap is headroom for growth; blowing it means the shard scheme
 * needs more shards, and we refuse to emit a truncated sitemap. */
const WALL_ART_SHARD_MAX_QUERIES = 20;

export async function listWallArtShard(opts: {
  start: string;
  end: string | null;
  revalidate?: number;
}): Promise<ShopProductRow[]> {
  const collected: ShopProductRow[] = [];
  let cursor = opts.start;
  let inclusive = true;

  for (let i = 0; i < WALL_ART_SHARD_MAX_QUERIES; i += 1) {
    const filterPairs: Array<[string, string]> = [
      ["status", "eq.published"],
      ["format", "eq.wall_art"],
      ["id", `${inclusive ? "gte" : "gt"}.${cursor}`],
    ];
    if (opts.end) filterPairs.push(["id", `lt.${opts.end}`]);

    const rows = await supabaseRest<ShopProductRow>(
      "shop_products",
      { filterPairs, limit: POSTGREST_MAX_ROWS, order: "id.asc" },
      opts.revalidate !== undefined ? { revalidate: opts.revalidate } : undefined,
    );

    collected.push(...rows);

    /* Short read means the range is exhausted — the only safe stop. */
    if (rows.length < POSTGREST_MAX_ROWS) {
      return collected.filter((r) => {
        const nt = r.metadata?.notion_template;
        return nt === undefined || nt === null;
      });
    }

    const last = rows[rows.length - 1];
    if (!last?.id) {
      throw new Error(
        "listWallArtShard: row without an id, cannot advance the keyset cursor",
      );
    }
    cursor = last.id;
    inclusive = false;
  }

  throw new Error(
    `listWallArtShard: shard exceeded ${WALL_ART_SHARD_MAX_QUERIES} queries ` +
      `(${WALL_ART_SHARD_MAX_QUERIES * POSTGREST_MAX_ROWS} rows). Raise ` +
      `WALL_ART_SHARD_COUNT. Refusing to emit a silently truncated sitemap.`,
  );
}
